import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { v4 as uuid } from 'uuid';

import { trackException } from '@fora-monorepo/webapp-analytics';
import { ChangeEmployerRequestInput } from '@fora-monorepo/auth-common';

import { envConfig } from '../../config';
import { createThunkError } from '../utils';
import { getEmployer } from '../employer/employerThunks';
import { clearSelectedEmployer } from '../employer/employerSlice';
import { mockAccessToken, mockRefreshToken } from './mocks';

export const getToken = createAsyncThunk(
  'getToken',
  async (
    data: {
      authCode: string;
      codeChallenge: string;
    },
    thunkAPI
  ) => {
    const { authCode, codeChallenge } = data;

    if (envConfig.mockAuthFrontend) {
      // Turn this on to get to multiview in mocked mode
      // return mockAccessToken.noSelectedOrganization;
      return mockAccessToken.selectedOrganization;
    }

    return axios
      .get(
        `${envConfig.authApiBaseUri}/public/token?authDomain=ORGANIZATION_SAPEP&grant_type=authorization_code&authorizationCode=${authCode}&verifyingString=${codeChallenge}`,
        { withCredentials: true }
      )
      .then(res => {
        if (res.status === 200) {
          const accessToken = res.data.accessToken;
          return { accessToken };
        }
      })
      .catch(err => {
        trackException(err);
        return thunkAPI.rejectWithValue(createThunkError('Could not get auth token', err));
      });
  }
);

export const getTokenForaKS = createAsyncThunk(
  'getTokenForaKS',
  async ({ partnerId, azureJwtToken }: { partnerId: string; azureJwtToken: string }, thunkAPI) => {
    if (envConfig.mockAuthFrontend) {
      return mockAccessToken.selectedOrganization;
    }

    try {
      const verifyingString: string = uuid();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data }: any = await axios.post(`${envConfig.authApiBaseUri}/ks/login`, {
        codeChallenge: verifyingString,
        partnerId,
        azureJwtToken,
        authDomain: 'ORGANIZATION_SAPEP',
      });
      const resultGetToken = await axios.get(
        `${envConfig.authApiBaseUri}/public/token?grant_type=authorization_code&authorizationCode=${data.authorizationCode}&verifyingString=${verifyingString}`,
        { withCredentials: true }
      );
      return { accessToken: resultGetToken.data.accessToken };
    } catch (err: any) {
      trackException(err);
      return thunkAPI.rejectWithValue(createThunkError('Could not get auth token', err));
    }
  }
);

export const changeEmployerAndGetToken = createAsyncThunk(
  'changeEmployerAndGetToken',
  async (
    data: {
      partnerNumberOrganization: string | null;
    },
    thunkAPI
  ) => {
    if (envConfig.mockAuthFrontend) {
      if (data.partnerNumberOrganization) {
        await thunkAPI.dispatch(getEmployer());
      } else {
        thunkAPI.dispatch(clearSelectedEmployer());
      }
      return;
    }

    try {
      const codeChallenge: string = uuid();
      const payload: ChangeEmployerRequestInput = {
        partnerNumberOrganization: data.partnerNumberOrganization,
        codeChallenge,
      };

      // Update login state
      const authCode = await axios
        .post(`${envConfig.authApiBaseUri}/public/employer-my-pages/change-employer`, payload)
        .then(res => {
          if (res.status === 200) {
            return res.data.authorizationCode;
          }
        });

      // Get new token
      await thunkAPI.dispatch(getToken({ codeChallenge, authCode }));

      // Get new employer data if changing to another, else clear it (multi mode)
      if (data.partnerNumberOrganization) {
        await thunkAPI.dispatch(getEmployer());
      } else {
        thunkAPI.dispatch(clearSelectedEmployer());
      }
      // TODO: Clear fetched employments, reports, etc... data that belongs to a specific employer
      // both when changing and when going multi
    } catch (err: any) {
      trackException(err);
      return thunkAPI.rejectWithValue(createThunkError('Could not change employer', err));
    }
  }
);

export const refreshToken = createAsyncThunk('refreshToken', async (_, thunkAPI) => {
  if (envConfig.mockAuthFrontend) {
    // Turn this on to get to multiview in mocked mode
    // return mockRefreshToken.noSelectedOrganization;
    return mockRefreshToken.selectedOrganization;
  }

  return axios
    .get(`${envConfig.authApiBaseUri}/public/token?authDomain=ORGANIZATION_SAPEP&grant_type=refresh_token`, {
      withCredentials: true,
      headers: {
        'x-selected-partner-number-organization': sessionStorage.getItem('selectedPartnerNumberOrganization'),
      },
    })
    .then(res => {
      if (res.status === 200) {
        const accessToken = res.data.accessToken;
        return { accessToken };
      }
    })
    .catch(err => {
      trackException(err);
      return thunkAPI.rejectWithValue(createThunkError('Could not refresh auth token', err));
    });
});
